import React from 'react';
import { flattenAllocationTree } from './ResourcePlanning';
import { PickupTag } from './MetricToggle';
import { PICKUP_NOTE, isPriced, money, pricePickups, rateAt, ratableAt } from './revenueLogic';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A node's month -> hours map priced at its project's rate for that month of `year`. */
export function priceMonths(hoursByMonth, projectId, year, rateIndex) {
  const out = {};
  for (const [m, h] of Object.entries(hoursByMonth || {})) {
    const v = (h || 0) * rateAt(rateIndex, projectId, year * 12 + (Number(m) - 1));
    if (v) out[m] = v;
  }
  return out;
}

const sumValues = (o) => Object.values(o).reduce((s, v) => s + v, 0);

/**
 * Read-only revenue twin of the Forecasting / Baselines grid: the same Project > Team > Person tree,
 * each cell hours x the project's billing rate for that month. Priced per node (not by scaling the
 * parent) so a rate change mid-year shows up in the right months at every level.
 */
export default function RevenueGrid({ tree, year, rateIndex, expanded, onToggle, pickups = [] }) {
  const pickupBy = new Map(pricePickups(pickups, rateIndex).filter((p) => p.year === year).map((p) => [`${p.projectId}|${p.month}`, p.revenue]));
  const shownPickups = tree.reduce((n, p) => n + [...Array(12).keys()].filter((i) => pickupBy.get(`${p.projectId}|${i + 1}`) > 0).length, 0);
  const rows = flattenAllocationTree(tree, expanded);
  const priced = (node) => priceMonths(node.hoursByMonth, node.projectId, year, rateIndex);
  // Pickup is project-level: it adds to the project row's total and the footer, not to team/person rows.
  // Ratable (flat $/month) is project-level the same way, added in on top of the pickup.
  // Totals are kept in two buckets: hours x rate (+ ratable) and pickup, shown on separate lines.
  const pickupOf = (node, m) => pickupBy.get(`${node.projectId}|${m}`) || 0;
  const ratableOf = (node, m) => ratableAt(rateIndex, node.projectId, year * 12 + (m - 1)) || 0;
  const sumMonths = (fn) => [...Array(12).keys()].reduce((s, i) => s + fn(i + 1), 0);
  const totals = {};
  const pickupTotals = {};
  const unpriced = [];
  for (const p of tree) {
    const v = priced(p);
    for (const [m, x] of Object.entries(v)) totals[m] = (totals[m] || 0) + x;
    for (let m = 1; m <= 12; m += 1) {
      if (ratableOf(p, m)) totals[m] = (totals[m] || 0) + ratableOf(p, m);
      if (pickupOf(p, m)) pickupTotals[m] = (pickupTotals[m] || 0) + pickupOf(p, m);
    }
    if (!isPriced(rateIndex, p.projectId) && sumValues(p.hoursByMonth || {}) > 0) unpriced.push(p.label);
  }
  const caret = (open) => (open ? '▾' : '▸');
  return (
    <>
      {shownPickups > 0 && <div className="up-legend"><span style={{ color: 'var(--accent-orange)', fontWeight: 700 }}>$ Pickup</span><span className="text-muted">{PICKUP_NOTE}</span></div>}
      {unpriced.length > 0 && <div className="up-banner">{unpriced.length} {unpriced.length === 1 ? 'project has' : 'projects have'} no billing rate and show as $0: {unpriced.join(', ')}.</div>}
      <div className="sheet-wrap" style={{ marginBottom: 16 }}>
        <table className="sheet">
          <thead>
            <tr>
              <th style={{ textAlign: 'left', minWidth: 200 }}>Project</th>
              <th style={{ textAlign: 'left', minWidth: 120 }}>Team</th>
              <th style={{ textAlign: 'left', minWidth: 150 }}>Person</th>
              {MONTH_NAMES.map((m) => <th key={m} className="cell-month" style={{ textAlign: 'center', width: 72 }}>{m}</th>)}
              <th className="cell-month" style={{ textAlign: 'center', width: 90 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((entry, index) => {
              const node = entry.node;
              const isGroup = entry.kind !== 'person';
              const v = priced(node);
              const leafIndex = rows.slice(0, index).filter((e) => e.kind === 'person').length;
              const cls = isGroup ? (entry.kind === 'project' ? 'is-group' : 'is-group is-group-sub') : (leafIndex % 2 === 1 ? 'is-alt' : '');
              return (
                <tr key={`${entry.kind}:${node.key || node.id}`} className={cls}>
                  <td style={{ paddingLeft: 10, cursor: entry.kind === 'project' ? 'pointer' : 'default' }} onClick={() => entry.kind === 'project' && onToggle(node.key)}>
                    {entry.kind === 'project' ? `${caret(entry.open)} ${node.label}` : ''}
                  </td>
                  <td style={{ paddingLeft: 26, cursor: entry.kind === 'team' && node.hasPeople ? 'pointer' : 'default' }} onClick={() => entry.kind === 'team' && node.hasPeople && onToggle(node.key)}>
                    {entry.kind === 'team' ? `${node.hasPeople ? caret(entry.open) : '　'} ${node.label}` : ''}
                  </td>
                  <td style={{ padding: '8px 10px 8px 42px' }}>{entry.kind === 'person' ? (node.userName || '—') : ''}</td>
                  {MONTH_NAMES.map((_, i) => <td key={i} className="cell-num cell-month" style={{ padding: '7px 4px' }}>{(() => { const x = (v[i + 1] || 0) + (entry.kind === 'project' ? ratableAt(rateIndex, node.projectId, year * 12 + i) || 0 : 0); return x ? money(x) : ''; })()}{entry.kind === 'project' && pickupBy.get(`${node.projectId}|${i + 1}`) > 0 && <PickupTag amount={pickupBy.get(`${node.projectId}|${i + 1}`)} />}</td>)}
                  <td className="cell-num cell-month" style={{ padding: '7px 4px', fontWeight: isGroup ? 600 : 400 }}>{(() => { const t = sumValues(v) + (entry.kind === 'project' ? sumMonths((m) => ratableOf(node, m)) : 0); return t ? money(t) : ''; })()}{entry.kind === 'project' && sumMonths((m) => pickupOf(node, m)) > 0 && <PickupTag label="" title="Total including post-go-live pickup" amount={sumValues(v) + sumMonths((m) => ratableOf(node, m)) + sumMonths((m) => pickupOf(node, m))} />}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="is-group is-total">
              <td colSpan={3} style={{ paddingLeft: 10 }}>Total</td>
              {MONTH_NAMES.map((_, i) => <td key={i} className="cell-num cell-month" style={{ padding: '7px 4px' }}>{totals[i + 1] ? money(totals[i + 1]) : ''}{pickupTotals[i + 1] > 0 && <PickupTag label="" title="Total including post-go-live pickup" amount={(totals[i + 1] || 0) + pickupTotals[i + 1]} />}</td>)}
              <td className="cell-num cell-month" style={{ padding: '7px 4px' }}>{sumValues(totals) ? money(sumValues(totals)) : ''}{sumValues(pickupTotals) > 0 && <PickupTag label="" title="Total including post-go-live pickup" amount={sumValues(totals) + sumValues(pickupTotals)} />}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}
